import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Square3Stack3DIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useGetVideoHeatmapRender, useGetVideoOriginalRender } from "@/api/renderer/queries";
import { VideoInsightScene } from "@/api/studio/insights/types/attention.type";
import { cn } from "@/utils/classes";
import { asPlaybackTime } from "@/utils/format";

type RenderVariant = "original" | "heatmap";

/* Prop Types */
interface VideoPlayerProps {
  projectId: string;
  studyId: string;
  videoKey: string;
  scenes: VideoInsightScene[];
}

const SKIP_SECONDS = 5;
const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

/**
 * VideoPlayer component
 *
 * Fetches a pre-signed playback URL for the current video and renders it with a custom control
 * bar matching the in-depth Figma design: a per-scene segmented progress bar, play/rewind/forward/
 * mute controls, a timestamp, a playback speed toggle, and fullscreen.
 */
export const VideoPlayer = ({ projectId, studyId, videoKey, scenes }: VideoPlayerProps) => {
  /* State */
  const [variant, setVariant] = useState<RenderVariant>("original");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRateIndex, setPlaybackRateIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* Queries */
  const { data: originalRender, isLoading: isOriginalLoading, isError: isOriginalError } = useGetVideoOriginalRender({ projectId, studyId, assetKey: videoKey });
  const {
    data: heatmapRender,
    isLoading: isHeatmapLoading,
    isError: isHeatmapError,
  } = useGetVideoHeatmapRender({ projectId, studyId, assetKey: videoKey }, { enabled: variant === "heatmap" });

  const render = variant === "heatmap" ? heatmapRender : originalRender;
  const isLoading = variant === "heatmap" ? isHeatmapLoading : isOriginalLoading;
  const isError = variant === "heatmap" ? isHeatmapError : isOriginalError;

  /* Refs */
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Carries playback position/state across a render-variant swap, since changing the <video>'s
  // src otherwise resets currentTime to 0 the same way switching to a different video should.
  const resumeRef = useRef({ time: 0, wasPlaying: false });

  /* Handlers */
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const toggleVariant = () => {
    const video = videoRef.current;
    resumeRef.current = { time: video?.currentTime ?? 0, wasPlaying: !!video && !video.paused };
    setVariant((current) => (current === "original" ? "heatmap" : "original"));
  };

  const skip = (deltaSeconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(video.currentTime + deltaSeconds, 0), video.duration || 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const cyclePlaybackRate = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextIndex = (playbackRateIndex + 1) % PLAYBACK_RATES.length;
    video.playbackRate = PLAYBACK_RATES[nextIndex];
    setPlaybackRateIndex(nextIndex);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  };

  /* Side Effects */
  useEffect(() => {
    setCurrentTime(0);
    setIsPlaying(false);
    setVariant("original");
    resumeRef.current = { time: 0, wasPlaying: false };
  }, [videoKey]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  /* Render */
  return (
    <div ref={containerRef} className="relative w-full aspect-[895/441] rounded-lg bg-[#323c49] overflow-hidden min-h-[450px]">
      {isLoading && <p className="absolute inset-0 flex items-center justify-center text-sm text-white/60">Loading video…</p>}
      {isError && <p className="absolute inset-0 flex items-center justify-center text-sm text-white/60">Unable to load video. Please try again.</p>}

      {render && (
        <>
          <video
            key={videoKey}
            ref={videoRef}
            data-testid="video-player-element"
            src={render.url}
            className="absolute inset-0 w-full h-full object-contain cursor-pointer"
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              setDuration(video.duration);

              const { time, wasPlaying } = resumeRef.current;
              if (time > 0) {
                video.currentTime = time;
                if (wasPlaying) video.play();
                resumeRef.current = { time: 0, wasPlaying: false };
              }
            }}
          />

          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-3 bg-gradient-to-t from-black/70 to-transparent rounded-b-lg">
            {/* Per-scene progress */}
            <div className="flex gap-1 w-full">
              {scenes.length === 0 ? (
                <div className="h-1.5 w-full rounded-full bg-[#e9e9ec] overflow-hidden">
                  <div className="h-full bg-[#f2318a]" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                </div>
              ) : (
                scenes.map((scene) => {
                  const playedPercent = currentTime <= scene.startTime ? 0 : currentTime >= scene.endTime ? 100 : ((currentTime - scene.startTime) / scene.duration) * 100;
                  return (
                    <div
                      key={scene.scene}
                      data-testid="scene-progress-segment"
                      className="h-1.5 rounded-full bg-[#e9e9ec] overflow-hidden"
                      style={{ width: `${(scene.duration / scenes.reduce((sum, s) => sum + s.duration, 0)) * 100}%` }}
                    >
                      <div className="h-full bg-[#f2318a]" style={{ width: `${playedPercent}%` }} />
                    </div>
                  );
                })
              )}
            </div>

            {/* Controls */}
            <div className="grid grid-cols-3 items-center w-full text-white">
              <div className="flex items-center gap-3 justify-self-start">
                <button type="button" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"} className="cursor-pointer">
                  {isPlaying ? <PauseIcon className="size-[26px]" /> : <PlayIcon className="size-[26px]" />}
                </button>
                <button type="button" onClick={() => skip(-SKIP_SECONDS)} aria-label="Rewind 5 seconds" className="cursor-pointer">
                  <ArrowUturnLeftIcon className="size-[18px]" />
                </button>
                <button type="button" onClick={() => skip(SKIP_SECONDS)} aria-label="Forward 5 seconds" className="cursor-pointer">
                  <ArrowUturnRightIcon className="size-[18px]" />
                </button>
                <button type="button" onClick={toggleMute} aria-label={isMuted ? "Unmute" : "Mute"} className="cursor-pointer">
                  {isMuted ? <SpeakerXMarkIcon className="size-[18px]" /> : <SpeakerWaveIcon className="size-[18px]" />}
                </button>
              </div>

              <p className="justify-self-center text-sm font-medium whitespace-nowrap">
                {asPlaybackTime(currentTime)} / {asPlaybackTime(duration)}
              </p>

              <div className="flex items-center gap-3 justify-self-end">
                <button type="button" onClick={cyclePlaybackRate} className="text-xs font-semibold cursor-pointer" aria-label="Change playback speed">
                  {PLAYBACK_RATES[playbackRateIndex]}x
                </button>
                <button
                  type="button"
                  onClick={toggleVariant}
                  aria-label={variant === "heatmap" ? "Switch to original video" : "Switch to attention heatmap"}
                  aria-pressed={variant === "heatmap"}
                  className="cursor-pointer"
                >
                  <Square3Stack3DIcon className={cn("size-[18px]", variant === "heatmap" ? "text-white" : "text-white/60")} />
                </button>
                <button type="button" onClick={toggleFullscreen} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} className="cursor-pointer">
                  {isFullscreen ? <ArrowsPointingInIcon className="size-[18px]" /> : <ArrowsPointingOutIcon className="size-[18px]" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
